'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Plus, Calendar, List } from 'lucide-react';
import WeeklyCalendarGrid from './WeeklyCalendarGrid';
import AppointmentListView from './AppointmentListView';
import CreateAppointmentModal from './CreateAppointmentModal';
import SaturdaySlotCounter from './SaturdaySlotCounter';
import { Appointment, Technician } from './schedulingTypes';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';

function getWeekDates(baseDate: Date): Date[] {
  const day = baseDate.getDay();
  const monday = new Date(baseDate);
  monday.setDate(baseDate.getDate() - (day === 0 ? 6 : day - 1));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function SchedulingContent() {
  const [currentDate] = useState(new Date());
  const [weekOffset, setWeekOffset] = useState(0);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const supabase = createClient();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [apptRes, techRes] = await Promise.all([
        supabase.from('appointments').select('*').order('scheduled_date', { ascending: true }).order('scheduled_time', { ascending: true }),
        supabase.from('technicians').select('id, full_name, status').eq('status', 'active'),
      ]);

      if (apptRes.error) { toast.error('Failed to load appointments'); }
      if (techRes.error) { toast.error('Failed to load technicians'); }

      const mappedAppts: Appointment[] = (apptRes.data || []).map((row) => ({
        id: row.id,
        customerNumber: row.customer_number,
        customerName: row.customer_name,
        address: row.address || '',
        landmark: row.landmark || '',
        machineModel: row.machine_model || '',
        scheduledDate: row.scheduled_date,
        scheduledTime: row.scheduled_time?.slice(0, 5) || '',
        assignedTechnicianId: row.assigned_technician_id || '',
        assignedTechnicianName: row.assigned_technician_name || '',
        paymentMode: row.payment_mode as Appointment['paymentMode'],
        filters: {
          sedi: row.filter_sedi,
          pre: row.filter_pre,
          mem: row.filter_mem,
          post: row.filter_post,
        },
        status: row.status as Appointment['status'],
        isSaturday: row.is_saturday,
        rebookingFee: Number(row.rebooking_fee),
        notes: row.notes || '',
      }));

      const mappedTechs: Technician[] = (techRes.data || []).map((row) => ({
        id: row.id,
        name: row.full_name,
        status: row.status as Technician['status'],
      }));

      setAppointments(mappedAppts);
      setTechnicians(mappedTechs);
    } catch (err) {
      toast.error('Failed to load scheduling data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const baseDate = new Date(currentDate);
  baseDate.setDate(currentDate.getDate() + weekOffset * 7);
  const weekDates = getWeekDates(baseDate);
  const weekLabel = `${MONTHS[weekDates[0].getMonth()]} ${weekDates[0].getDate()} – ${weekDates[6].getDate()}, ${weekDates[0].getFullYear()}`;

  const saturdayAppts = appointments.filter((a) => a.isSaturday && a.status !== 'cancelled');

  function formatDateKey(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  async function handleSaveAppointment(appt: Appointment) {
    const { error } = await supabase.from('appointments').insert({
      customer_number: appt.customerNumber,
      customer_name: appt.customerName,
      address: appt.address,
      landmark: appt.landmark,
      machine_model: appt.machineModel,
      scheduled_date: appt.scheduledDate,
      scheduled_time: appt.scheduledTime,
      assigned_technician_id: appt.assignedTechnicianId || null,
      assigned_technician_name: appt.assignedTechnicianName,
      payment_mode: appt.paymentMode,
      filter_sedi: appt.filters.sedi,
      filter_pre: appt.filters.pre,
      filter_mem: appt.filters.mem,
      filter_post: appt.filters.post,
      status: appt.status,
      is_saturday: appt.isSaturday,
      rebooking_fee: appt.rebookingFee,
      notes: appt.notes,
    });

    if (error) {
      toast.error('Failed to save appointment: ' + error.message);
      return;
    }
    toast.success(`Appointment scheduled for ${appt.customerName}`);
    setCreateModalOpen(false);
    fetchData();
  }

  return (
    <div className="px-6 py-5 max-w-screen-2xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-700 text-foreground">Scheduling & Booking</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Mon–Fri 8:00 AM – 5:00 PM · Saturday max 3 slots · ₱300 reschedule fee
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SaturdaySlotCounter used={saturdayAppts.length} max={3} />
          <div className="flex items-center bg-muted rounded-lg p-1">
            <button
              onClick={() => setViewMode('calendar')}
              className={`p-1.5 rounded transition-colors ${viewMode === 'calendar' ? 'bg-card shadow text-primary' : 'text-muted-foreground hover:text-foreground'}`}
              aria-label="Calendar view"
            >
              <Calendar size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? 'bg-card shadow text-primary' : 'text-muted-foreground hover:text-foreground'}`}
              aria-label="List view"
            >
              <List size={16} />
            </button>
          </div>
          <button
            onClick={() => { setSelectedDate(null); setCreateModalOpen(true); }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-600 btn-primary rounded-lg"
          >
            <Plus size={16} />
            New Appointment
          </button>
        </div>
      </div>

      {/* Week navigation */}
      <div className="bg-card rounded-xl border border-border p-4 flex items-center justify-between">
        <button
          onClick={() => setWeekOffset((o) => o - 1)}
          className="p-2 rounded-lg hover:bg-muted transition-colors border border-border"
          aria-label="Previous week"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="text-center">
          <p className="text-base font-700 text-foreground">{weekLabel}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {appointments.filter((a) => {
              const d = weekDates.find((wd) => formatDateKey(wd) === a.scheduledDate);
              return !!d;
            }).length} appointments this week
          </p>
        </div>
        <button
          onClick={() => setWeekOffset((o) => o + 1)}
          className="p-2 rounded-lg hover:bg-muted transition-colors border border-border"
          aria-label="Next week"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Technician load summary */}
      <TechnicianLoadSummary technicians={technicians} appointments={appointments} weekDates={weekDates} formatDateKey={formatDateKey} />

      {/* Calendar / List */}
      {loading ? (
        <div className="bg-card rounded-xl border border-border p-8 text-center text-sm text-muted-foreground">
          Loading appointments...
        </div>
      ) : viewMode === 'calendar' ? (
        <WeeklyCalendarGrid
          weekDates={weekDates}
          appointments={appointments}
          days={DAYS}
          formatDateKey={formatDateKey}
          onCreateForDate={(dateStr) => { setSelectedDate(dateStr); setCreateModalOpen(true); }}
        />
      ) : (
        <AppointmentListView
          appointments={appointments}
          weekDates={weekDates}
          formatDateKey={formatDateKey}
          days={DAYS}
        />
      )}

      <CreateAppointmentModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        technicians={technicians}
        preselectedDate={selectedDate}
        saturdaySlotsUsed={saturdayAppts.length}
        onSave={handleSaveAppointment}
      />
    </div>
  );
}

function TechnicianLoadSummary({
  technicians,
  appointments,
  weekDates,
  formatDateKey,
}: {
  technicians: Technician[];
  appointments: Appointment[];
  weekDates: Date[];
  formatDateKey: (d: Date) => string;
}) {
  const weekDateKeys = weekDates.map(formatDateKey);

  if (technicians.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border p-4 text-center text-sm text-muted-foreground">
        No technicians registered yet — add technicians to see daily load summary
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-4">
      <h3 className="text-sm font-700 text-foreground mb-3">Technician Load This Week</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {technicians.map((tech) => {
          const weekJobs = appointments.filter(
            (a) => a.assignedTechnicianId === tech.id && weekDateKeys.includes(a.scheduledDate)
          ).length;
          return (
            <div key={tech.id} className="bg-muted rounded-lg p-3">
              <p className="text-xs font-600 text-foreground truncate">{tech.name}</p>
              <p className="text-xl font-700 text-primary tabular-nums mt-1">{weekJobs}</p>
              <p className="text-xs text-muted-foreground">jobs this week</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}